/**
 * O cartão "Novo Lançamento" da aba Outros Bancos.
 *
 * Papel: reproduz o formulário de l. 515–548 e `adicionarLancamentoOutrosBancos`
 * (l. 3963–4030): Favorecido e Data; Categoria e Subcategoria; Tipo, Forma de
 * Pagamento e Valor; Descrição; e "+ Adicionar lançamento", que grava um
 * lançamento fora do C6 no mês aberto.
 *
 * Como usar: quem monta passa a casa e o mês aberto, e só monta o cartão para
 * quem tem `editar`. O cartão fica montado o tempo todo, como o DOM escondido
 * da origem: o que foi digitado continua ao trocar de aba, e trocar de mês não
 * mexe nos campos. A data começa no dia 1 do mês aberto e só volta a ele quando
 * a pessoa a deixou vazia e o mês muda.
 *
 * O lançamento vai para o mês aberto mesmo com a data digitada em outro mês,
 * como na origem. O favorecido é texto, e não vínculo com o cadastro: o nome
 * novo não entra na lista de Cadastros. As categorias seguem o tipo — as de
 * receita para "↙ Entrada" e as de despesa para "↗ Despesa" — e a subcategoria
 * segue a categoria; trocar o tipo leva à primeira categoria dele.
 */
import {
  type FormaPagamentoLancamento,
  ROTULOS_FORMA,
  type TipoLancamentoOutrosBancos,
} from '@concierge/shared';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { lerValorDigitado, mascararCentavos } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import { categoriasDoTipo, subcategoriasDoTipo } from '../formulario-do-lancamento';
import { useFavorecidos, usePlanoDeContas } from '../hooks/usar-financas';
import { useCriarLancamentoDeOutrosBancos } from '../hooks/usar-financas-extras';
import { Botao } from './kit/botao';
import { CLASSE_DO_CONTROLE, CLASSE_DO_SELECT, Campo, LinhaDeCampos } from './kit/campo';
import { CabecalhoDoCartao, Cartao, CorpoDoCartao, TituloDoCartao } from './kit/cartao';

/** As sete formas, na ordem do `select` de Outros Bancos, com Transferência primeiro (l. 538). */
const FORMAS_DE_OUTROS_BANCOS: readonly FormaPagamentoLancamento[] = [
  'transferencia',
  'pix',
  'boleto',
  'cartao_de_credito',
  'dinheiro',
  'debito_automatico',
  'cartao_de_debito',
];

/** Os dois tipos e o texto do `select` (l. 532–533). */
const TIPOS_DE_OUTROS_BANCOS: readonly {
  valor: TipoLancamentoOutrosBancos;
  rotulo: string;
}[] = [
  { valor: 'despesa', rotulo: '↗ Despesa' },
  { valor: 'receita', rotulo: '↙ Entrada' },
];

/** Os campos do formulário, como estão na tela. */
interface CamposDoLancamento {
  favorecido: string;
  data: string;
  tipo: TipoLancamentoOutrosBancos;
  categoria: string;
  subcategoria: string;
  formaPagamento: FormaPagamentoLancamento;
  valor: string;
  descricao: string;
}

interface CartaoNovoLancamentoProps {
  residenciaId: string;
  /** O mês aberto, em `AAAA-MM`. */
  competencia: string;
}

/**
 * Ordena nomes como a origem ordena as listas do formulário.
 *
 * @param nomes - Os nomes.
 * @returns Os nomes em `localeCompare('pt-BR', { sensitivity: 'base' })`.
 */
function emOrdem(nomes: readonly string[]): string[] {
  return [...nomes].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * O cartão "Novo Lançamento".
 *
 * @param residenciaId - A casa do caixa.
 * @param competencia - O mês aberto, para onde o lançamento vai.
 * @returns O cartão com o formulário.
 */
export function CartaoNovoLancamento({
  residenciaId,
  competencia,
}: CartaoNovoLancamentoProps) {
  const notificar = useNotificacao();
  const plano = usePlanoDeContas().data ?? [];
  const favorecidos = useFavorecidos(residenciaId).data ?? [];
  const criar = useCriarLancamentoDeOutrosBancos();
  const [campos, setCampos] = useState<CamposDoLancamento>({
    favorecido: '',
    data: `${competencia}-01`,
    tipo: 'despesa',
    categoria: '',
    subcategoria: '',
    formaPagamento: 'transferencia',
    valor: '',
    descricao: '',
  });
  const [mesDaData, setMesDaData] = useState(competencia);

  // A data vazia volta ao dia 1 quando o mês aberto muda (l. 3999–4000).
  if (mesDaData !== competencia) {
    setMesDaData(competencia);
    if (campos.data === '') setCampos({ ...campos, data: `${competencia}-01` });
  }

  const categorias = emOrdem(categoriasDoTipo(plano, campos.tipo));
  const categoria = categorias.includes(campos.categoria)
    ? campos.categoria
    : (categorias[0] ?? '');
  const subcategorias = emOrdem(subcategoriasDoTipo(plano, campos.tipo, categoria));
  const subcategoria = subcategorias.includes(campos.subcategoria) ? campos.subcategoria : '';
  const nomes = emOrdem(favorecidos.map((favorecido) => favorecido.nome));

  /**
   * Troca campos do formulário.
   *
   * @param mudanca - Os campos que mudaram.
   */
  function handleMudar(mudanca: Partial<CamposDoLancamento>) {
    setCampos((atuais) => ({ ...atuais, ...mudanca }));
  }

  /** "+ Adicionar lançamento" (l. 4005–4030). */
  async function handleAdicionar() {
    const valor = lerValorDigitado(campos.valor);
    const descricao = campos.descricao.trim();
    if (campos.data === '' || descricao === '' || !(valor > 0)) {
      notificar('Preencha data, descrição e valor.', false);
      return;
    }
    try {
      await criar.mutateAsync({
        residenciaId,
        competencia,
        data: campos.data,
        tipo: campos.tipo,
        categoria,
        subcategoria: subcategoria === '' ? null : subcategoria,
        favorecido: campos.favorecido.trim() === '' ? null : campos.favorecido.trim(),
        formaPagamento: campos.formaPagamento,
        valor,
        descricao,
      });
      handleMudar({ favorecido: '', descricao: '', valor: '' });
      notificar('✓ Lançamento adicionado');
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <Cartao>
      <CabecalhoDoCartao>
        <TituloDoCartao>Novo Lançamento</TituloDoCartao>
        <span className="text-[11px] text-texto-fraco">
          Mesmo plano de contas — não gera lançamento real no C6
        </span>
      </CabecalhoDoCartao>
      <CorpoDoCartao className="px-4 py-3">
        <LinhaDeCampos colunas={2}>
          <Campo rotulo="Favorecido" id="ob-lc-favorecido">
            <input
              id="ob-lc-favorecido"
              type="text"
              list="ob-lc-favorecidos"
              placeholder="Selecione ou digite..."
              autoComplete="off"
              value={campos.favorecido}
              onChange={(evento) => handleMudar({ favorecido: evento.target.value })}
              className={CLASSE_DO_CONTROLE}
            />
            <datalist id="ob-lc-favorecidos">
              {nomes.map((nome) => (
                <option key={nome} value={nome} />
              ))}
            </datalist>
          </Campo>
          <Campo rotulo="Data" id="ob-lc-data">
            <input
              id="ob-lc-data"
              type="date"
              value={campos.data}
              onChange={(evento) => handleMudar({ data: evento.target.value })}
              className={CLASSE_DO_CONTROLE}
            />
          </Campo>
        </LinhaDeCampos>
        <LinhaDeCampos colunas={2}>
          <Campo rotulo="Categoria" id="ob-lc-categoria">
            <select
              id="ob-lc-categoria"
              value={categoria}
              onChange={(evento) =>
                handleMudar({ categoria: evento.target.value, subcategoria: '' })
              }
              className={CLASSE_DO_SELECT}
            >
              {categorias.map((nome) => (
                <option key={nome} value={nome}>
                  {nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Subcategoria" id="ob-lc-subcategoria">
            <select
              id="ob-lc-subcategoria"
              value={subcategoria}
              onChange={(evento) => handleMudar({ subcategoria: evento.target.value })}
              className={CLASSE_DO_SELECT}
            >
              <option value="">— Selecionar —</option>
              {subcategorias.map((nome) => (
                <option key={nome} value={nome}>
                  {nome}
                </option>
              ))}
            </select>
          </Campo>
        </LinhaDeCampos>
        <LinhaDeCampos colunas={3}>
          <Campo rotulo="Tipo" id="ob-lc-tipo">
            <select
              id="ob-lc-tipo"
              value={campos.tipo}
              onChange={(evento) => {
                const tipo = TIPOS_DE_OUTROS_BANCOS.find(
                  (opcao) => opcao.valor === evento.target.value,
                )?.valor;
                if (tipo !== undefined) handleMudar({ tipo, categoria: '', subcategoria: '' });
              }}
              className={CLASSE_DO_SELECT}
            >
              {TIPOS_DE_OUTROS_BANCOS.map((opcao) => (
                <option key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Forma de Pagamento" id="ob-lc-forma">
            <select
              id="ob-lc-forma"
              value={campos.formaPagamento}
              onChange={(evento) => {
                const forma = FORMAS_DE_OUTROS_BANCOS.find(
                  (opcao) => opcao === evento.target.value,
                );
                if (forma !== undefined) handleMudar({ formaPagamento: forma });
              }}
              className={CLASSE_DO_SELECT}
            >
              {FORMAS_DE_OUTROS_BANCOS.map((forma) => (
                <option key={forma} value={forma}>
                  {ROTULOS_FORMA[forma]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Valor (R$)" id="ob-lc-valor">
            <input
              id="ob-lc-valor"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={campos.valor}
              onChange={(evento) =>
                handleMudar({ valor: mascararCentavos(evento.target.value) })
              }
              className={cn(CLASSE_DO_CONTROLE, 'text-right')}
            />
          </Campo>
        </LinhaDeCampos>
        <Campo rotulo="Descrição" id="ob-lc-descricao">
          <input
            id="ob-lc-descricao"
            type="text"
            placeholder="Ex: Conserto elétrico — conta do pagador"
            value={campos.descricao}
            onChange={(evento) => handleMudar({ descricao: evento.target.value })}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
        <div className="mt-2.5">
          <Botao
            tamanho="sm"
            onClick={() => void handleAdicionar()}
            disabled={criar.isPending}
          >
            + Adicionar lançamento
          </Botao>
        </div>
      </CorpoDoCartao>
    </Cartao>
  );
}
